import React, { useEffect, useState } from "react";
import api from "../../api/api";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";

import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import GroupsIcon from "@mui/icons-material/Groups";
import PublicIcon from "@mui/icons-material/Public";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SecurityIcon from "@mui/icons-material/Security";
import LockResetIcon from "@mui/icons-material/LockReset";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const COLORS = {
  navy: "#102A43",
  deepNavy: "#071A2B",
  blue: "#1769AA",
  brightBlue: "#2185D0",

  green: "#16834B",
  brightGreen: "#22A861",
  softGreen: "#EEF8F2",
  paleGreen: "#F4FAF6",

  gold: "#E5A91A",
  brightGold: "#F4C542",
  softGold: "#FFF8E1",

  white: "#FFFFFF",
  cream: "#F7F9FB",

  text: "#172B3A",
  muted: "#6B7A86",
  border: "#DCE5EC",

  red: "#B42318",
  paleRed: "#FEF3F2",
};

const VerifyCode = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { email } = location.state || {};

  useEffect(() => {
    if (!email) {
      setMessage(
        "No email address was found. Please return to password recovery and request a new verification code."
      );
    }
  }, [email]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!email) {
      setMessage(
        "No email address was found. Please return to password recovery."
      );
      return;
    }

    if (code.length !== 6) {
      setMessage("Please enter the complete 6-digit verification code.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/verify-reset-code/", {
        email,
        code,
      });

      setMessage(
        res.data.message || "Verification successful."
      );

      setTimeout(() => {
        navigate("/resetPassword", {
          state: {
            email,
            code,
          },
        });
      }, 900);
    } catch (err) {
      setMessage(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "The verification code is invalid or has expired. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const floatingIcons = [
    {
      Icon: VolunteerActivismIcon,
      top: "12%",
      left: "8%",
      size: 30,
      delay: 0,
    },
    {
      Icon: GroupsIcon,
      top: "21%",
      right: "9%",
      size: 30,
      delay: 0.8,
    },
    {
      Icon: PublicIcon,
      bottom: "18%",
      left: "9%",
      size: 31,
      delay: 1.4,
    },
    {
      Icon: FavoriteIcon,
      bottom: "10%",
      right: "10%",
      size: 29,
      delay: 2,
    },
    {
      Icon: SecurityIcon,
      top: "46%",
      left: "4%",
      size: 25,
      delay: 2.5,
    },
    {
      Icon: CheckCircleIcon,
      top: "50%",
      right: "5%",
      size: 25,
      delay: 3,
    },
  ];

  const isSuccess = message
    .toLowerCase()
    .includes("successful");

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: {
          xs: 1.5,
          sm: 3,
        },
        py: {
          xs: 3,
          md: 5,
        },

        background: `
          radial-gradient(
            circle at 12% 15%,
            rgba(33,133,208,.10),
            transparent 30%
          ),
          radial-gradient(
            circle at 88% 85%,
            rgba(22,131,75,.10),
            transparent 30%
          ),
          linear-gradient(
            135deg,
            #FFFFFF 0%,
            #F7F9FB 50%,
            #F1F7FC 100%
          )
        `,

        "@keyframes floatOne": {
          "0%, 100%": {
            transform: "translate3d(0,0,0) scale(1)",
          },
          "50%": {
            transform: "translate3d(25px,-30px,0) scale(1.06)",
          },
        },

        "@keyframes floatTwo": {
          "0%, 100%": {
            transform: "translate3d(0,0,0)",
          },
          "50%": {
            transform: "translate3d(-25px,25px,0)",
          },
        },

        "@keyframes pulseGlow": {
          "0%, 100%": {
            opacity: 0.35,
            transform: "scale(1)",
          },
          "50%": {
            opacity: 0.9,
            transform: "scale(1.18)",
          },
        },

        "@keyframes rotateSlow": {
          "0%": {
            transform: "rotate(0deg)",
          },
          "100%": {
            transform: "rotate(360deg)",
          },
        },

        "@keyframes gridMove": {
          "0%": {
            backgroundPosition: "0 0",
          },
          "100%": {
            backgroundPosition: "50px 50px",
          },
        },
      }}
    >
      {/* Background Glow */}

      <Box
        sx={{
          position: "absolute",
          width: {
            xs: 220,
            md: 430,
          },
          height: {
            xs: 220,
            md: 430,
          },
          borderRadius: "50%",
          background: "rgba(33,133,208,.08)",
          filter: "blur(5px)",
          top: "-130px",
          left: "-110px",
          animation: "floatOne 16s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: {
            xs: 240,
            md: 450,
          },
          height: {
            xs: 240,
            md: 450,
          },
          borderRadius: "50%",
          background: "rgba(22,131,75,.08)",
          filter: "blur(7px)",
          bottom: "-160px",
          right: "-130px",
          animation: "floatTwo 18s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      {/* Background Grid */}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.3,
          pointerEvents: "none",
          backgroundImage: `
            linear-gradient(
              rgba(16,42,67,.045) 1px,
              transparent 1px
            ),
            linear-gradient(
              90deg,
              rgba(16,42,67,.045) 1px,
              transparent 1px
            )
          `,
          backgroundSize: "48px 48px",
          animation: "gridMove 28s linear infinite",
          maskImage:
            "linear-gradient(to bottom, transparent, black 22%, black 78%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 22%, black 78%, transparent)",
        }}
      />

      {/* Decorative Orbits */}

      <Box
        sx={{
          position: "absolute",
          width: {
            xs: 300,
            md: 520,
          },
          height: {
            xs: 300,
            md: 520,
          },
          borderRadius: "50%",
          border: "1px solid rgba(229,169,26,.10)",
          animation: "rotateSlow 38s linear infinite",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: {
            xs: 230,
            md: 410,
          },
          height: {
            xs: 230,
            md: 410,
          },
          borderRadius: "50%",
          border: "1px dashed rgba(33,133,208,.13)",
          animation:
            "rotateSlow 30s linear infinite reverse",
          pointerEvents: "none",
        }}
      />

      {/* Floating Organization Icons */}

      {floatingIcons.map(
        ({ Icon, delay, size, ...position }, index) => (
          <motion.div
            key={index}
            animate={{
              y: [0, -12, 0],
              rotate: [
                0,
                index % 2 === 0 ? 5 : -5,
                0,
              ],
              opacity: [0.28, 0.65, 0.28],
            }}
            transition={{
              duration: 4 + index * 0.35,
              repeat: Infinity,
              delay,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              ...position,
              zIndex: 0,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "rgba(255,255,255,.72)",
                border:
                  "1px solid rgba(16,42,67,.08)",
                boxShadow:
                  "0 10px 30px rgba(16,42,67,.06)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Icon
                sx={{
                  fontSize: size,
                  color:
                    index % 2 === 0
                      ? COLORS.blue
                      : COLORS.green,
                }}
              />
            </Box>
          </motion.div>
        )
      )}

      {/* Small Background Particles */}

      {[...Array(24)].map((_, index) => (
        <motion.div
          key={`particle-${index}`}
          animate={{
            y: [0, -15, 0],
            opacity: [0.12, 0.55, 0.12],
            scale: [0.8, 1.15, 0.8],
          }}
          transition={{
            duration: 3 + (index % 5),
            repeat: Infinity,
            delay: index * 0.17,
            ease: "easeInOut",
          }}
          style={{
            position: "absolute",
            width: `${2 + (index % 3)}px`,
            height: `${2 + (index % 3)}px`,
            borderRadius: "50%",
            background:
              index % 3 === 0
                ? COLORS.blue
                : index % 3 === 1
                ? COLORS.gold
                : COLORS.green,
            top: `${(index * 19) % 100}%`,
            left: `${(index * 31) % 100}%`,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Main Card */}

      <motion.div
        initial={{
          opacity: 0,
          y: 45,
          scale: 0.97,
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
        }}
        transition={{
          duration: 0.75,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={{
          width: "100%",
          maxWidth: "550px",
          zIndex: 5,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            p: {
              xs: 2.5,
              sm: 4,
              md: 4.5,
            },
            borderRadius: {
              xs: "22px",
              md: "28px",
            },
            background:
              "rgba(255,255,255,.95)",
            backdropFilter: "blur(24px)",
            border:
              "1px solid rgba(16,42,67,.09)",
            boxShadow: `
              0 30px 80px rgba(16,42,67,.10),
              0 8px 30px rgba(16,42,67,.05)
            `,
          }}
        >
          {/* Top Brand Accent */}

          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "5px",
              background: `
                linear-gradient(
                  90deg,
                  ${COLORS.deepNavy},
                  ${COLORS.blue},
                  ${COLORS.green},
                  ${COLORS.brightGold},
                  ${COLORS.deepNavy}
                )
              `,
            }}
          />

          {/* Brand */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.85,
                borderRadius: "999px",
                background: COLORS.paleGreen,
                border:
                  `1px solid ${COLORS.border}`,
              }}
            >
              <VolunteerActivismIcon
                sx={{
                  fontSize: 19,
                  color: COLORS.green,
                }}
              />

              <Typography
                sx={{
                  fontSize: {
                    xs: ".63rem",
                    sm: ".7rem",
                  },
                  fontWeight: 900,
                  letterSpacing: ".1em",
                  color: COLORS.navy,
                }}
              >
                PASSION AIDS THE FUTURE
              </Typography>
            </Box>
          </Box>

          {/* Verification Icon */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <motion.div
              animate={{
                y: [0, -8, 0],
                rotate: [0, 2, -2, 0],
              }}
              transition={{
                duration: 3.2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: {
                    xs: 86,
                    md: 98,
                  },
                  height: {
                    xs: 86,
                    md: 98,
                  },
                  borderRadius: "26px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `
                    linear-gradient(
                      145deg,
                      ${COLORS.deepNavy},
                      ${COLORS.blue}
                    )
                  `,
                  boxShadow:
                    "0 18px 40px rgba(16,42,67,.20)",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 7,
                    borderRadius: "20px",
                    border:
                      "1px solid rgba(255,255,255,.20)",
                  }}
                />

                <VerifiedUserIcon
                  sx={{
                    fontSize: {
                      xs: 46,
                      md: 54,
                    },
                    color: COLORS.white,
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    width: 13,
                    height: 13,
                    borderRadius: "50%",
                    background: COLORS.brightGold,
                    top: 8,
                    right: 8,
                    boxShadow:
                      `0 0 18px ${COLORS.brightGold}`,
                    animation:
                      "pulseGlow 2s ease-in-out infinite",
                  }}
                />
              </Box>
            </motion.div>
          </Box>

          {/* Heading */}

          <Box textAlign="center">
            <Typography
              sx={{
                fontSize: {
                  xs: "1.75rem",
                  sm: "2.1rem",
                  md: "2.3rem",
                },
                fontWeight: 1000,
                lineHeight: 1.1,
                letterSpacing: "-.035em",
                color: COLORS.navy,
              }}
            >
              Verify Your Code
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 1.4,
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 55,
                  height: 4,
                  borderRadius: 999,
                  background: `
                    linear-gradient(
                      90deg,
                      ${COLORS.blue},
                      ${COLORS.green},
                      ${COLORS.gold}
                    )
                  `,
                }}
              />
            </Box>

            <Typography
              sx={{
                color: COLORS.muted,
                fontSize: {
                  xs: ".87rem",
                  sm: ".93rem",
                },
                lineHeight: 1.7,
                maxWidth: 425,
                mx: "auto",
              }}
            >
              Enter the 6-digit verification code we sent
              to your email address to securely continue
              resetting your password.
            </Typography>

            {/* Email */}

            {email && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  px: 2,
                  py: 1,
                  borderRadius: "12px",
                  background: COLORS.softGreen,
                  border:
                    `1px solid ${COLORS.border}`,
                  maxWidth: "100%",
                }}
              >
                <EmailIcon
                  sx={{
                    fontSize: 18,
                    color: COLORS.green,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: ".82rem",
                    fontWeight: 750,
                    color: COLORS.navy,
                    wordBreak: "break-all",
                  }}
                >
                  {email}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Security Notice */}

          <Alert
            icon={
              <LockResetIcon
                sx={{
                  color: COLORS.blue,
                }}
              />
            }
            severity="info"
            sx={{
              mt: 3,
              borderRadius: "15px",
              background: COLORS.softGreen,
              border:
                `1px solid ${COLORS.border}`,
              color: COLORS.text,

              "& .MuiAlert-icon": {
                alignItems: "center",
              },

              "& .MuiAlert-message": {
                fontSize: ".81rem",
                lineHeight: 1.6,
              },
            }}
          >
            Your verification code is valid for 10 minutes.
            For your security, do not share this code with
            anyone.
          </Alert>

          {/* Form */}

          <Box
            component="form"
            onSubmit={handleVerify}
            sx={{
              mt: 3,
            }}
          >
            <TextField
              fullWidth
              required
              label="6-Digit Verification Code"
              value={code}
              disabled={loading}
              autoComplete="one-time-code"
              inputMode="numeric"
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onChange={(e) => {
                const value =
                  e.target.value.replace(/\D/g, "");

                setCode(value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockResetIcon
                      sx={{
                        color: COLORS.blue,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,

                "& .MuiInputLabel-root": {
                  color: COLORS.muted,
                  fontWeight: 600,
                },

                "& .MuiInputLabel-root.Mui-focused": {
                  color: COLORS.blue,
                },

                "& .MuiOutlinedInput-root": {
                  minHeight: 62,
                  borderRadius: "15px",
                  background: COLORS.white,
                  transition: "all .25s ease",

                  "& fieldset": {
                    borderColor: COLORS.border,
                  },

                  "&:hover fieldset": {
                    borderColor:
                      "rgba(33,133,208,.40)",
                  },

                  "&.Mui-focused": {
                    boxShadow:
                      "0 0 0 4px rgba(33,133,208,.09)",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: COLORS.blue,
                    borderWidth: "1.5px",
                  },
                },

                "& input": {
                  fontSize: {
                    xs: "1.2rem",
                    sm: "1.4rem",
                  },
                  fontWeight: 850,
                  letterSpacing: ".42em",
                  color: COLORS.navy,
                  textAlign: "center",
                  paddingLeft: "12px",
                },
              }}
            />

            {/* Verify Button */}

            <motion.div
              whileHover={{
                scale: loading ? 1 : 1.012,
              }}
              whileTap={{
                scale: loading ? 1 : 0.985,
              }}
            >
              <Button
                fullWidth
                type="submit"
                disabled={loading || !email}
                startIcon={
                  !loading ? (
                    <VerifiedUserIcon />
                  ) : null
                }
                sx={{
                  minHeight: 57,
                  borderRadius: "14px",
                  fontSize: ".9rem",
                  fontWeight: 900,
                  letterSpacing: ".01em",
                  color: COLORS.white,

                  background: `
                    linear-gradient(
                      135deg,
                      ${COLORS.deepNavy} 0%,
                      ${COLORS.blue} 52%,
                      ${COLORS.green} 100%
                    )
                  `,

                  boxShadow:
                    "0 14px 30px rgba(16,42,67,.18)",

                  transition:
                    "all .25s ease",

                  "&:hover": {
                    background: `
                      linear-gradient(
                        135deg,
                        ${COLORS.blue},
                        ${COLORS.green}
                      )
                    `,
                    boxShadow:
                      "0 18px 36px rgba(16,42,67,.23)",
                  },

                  "&:disabled": {
                    color:
                      "rgba(255,255,255,.86)",
                    background:
                      "linear-gradient(135deg,#71808B,#82919C)",
                  },
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                    }}
                  >
                    <CircularProgress
                      size={23}
                      thickness={5}
                      sx={{
                        color: COLORS.white,
                      }}
                    />
                    Verifying Code...
                  </Box>
                ) : (
                  "Verify Code"
                )}
              </Button>
            </motion.div>

            {/* Back Button */}

            <Button
              fullWidth
              type="button"
              startIcon={<ArrowBackIcon />}
              disabled={loading}
              onClick={() =>
                navigate("/forgotPassword")
              }
              sx={{
                mt: 1.3,
                minHeight: 47,
                borderRadius: "12px",
                color: COLORS.muted,
                fontSize: ".78rem",
                fontWeight: 800,
                textTransform: "none",

                "&:hover": {
                  background: COLORS.softGreen,
                  color: COLORS.navy,
                },
              }}
            >
              Back to Password Recovery
            </Button>
          </Box>

          {/* Message */}

          {message && (
            <motion.div
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.3,
              }}
            >
              <Box
                sx={{
                  mt: 2.3,
                  px: 2,
                  py: 1.45,
                  borderRadius: "13px",
                  background: isSuccess
                    ? COLORS.softGreen
                    : COLORS.paleRed,
                  border: isSuccess
                    ? "1px solid #B9E4C7"
                    : "1px solid #F4C7C3",
                }}
              >
                <Typography
                  textAlign="center"
                  sx={{
                    fontSize: ".8rem",
                    lineHeight: 1.5,
                    fontWeight: 700,
                    color: isSuccess
                      ? COLORS.green
                      : COLORS.red,
                  }}
                >
                  {message}
                </Typography>
              </Box>
            </motion.div>
          )}

          {/* Recovery Steps */}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "repeat(3, 1fr)",
              gap: 1,
              mt: 3,
            }}
          >
            {[
              {
                number: "01",
                title: "EMAIL",
                active: false,
              },
              {
                number: "02",
                title: "VERIFY",
                active: true,
              },
              {
                number: "03",
                title: "RESTORE",
                active: false,
              },
            ].map((step) => (
              <Box
                key={step.number}
                sx={{
                  p: 1.1,
                  textAlign: "center",
                  borderRadius: "10px",
                  background: step.active
                    ? COLORS.softGreen
                    : "#F8FAFC",
                  border: step.active
                    ? "1px solid #C8E6D1"
                    : `1px solid ${COLORS.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: ".56rem",
                    fontWeight: 950,
                    letterSpacing: 1,
                    color: step.active
                      ? COLORS.green
                      : COLORS.muted,
                  }}
                >
                  {step.number}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.3,
                    fontSize: ".62rem",
                    fontWeight: 900,
                    letterSpacing: ".05em",
                    color: step.active
                      ? COLORS.navy
                      : COLORS.muted,
                  }}
                >
                  {step.title}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Footer */}

          <Box
            sx={{
              mt: 3,
              pt: 2.3,
              borderTop:
                `1px solid ${COLORS.border}`,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.7,
              }}
            >
              <VolunteerActivismIcon
                sx={{
                  fontSize: 15,
                  color: COLORS.green,
                }}
              />

              <Typography
                sx={{
                  fontSize: ".66rem",
                  fontWeight: 900,
                  letterSpacing: ".1em",
                  color: COLORS.navy,
                }}
              >
                PASSION AIDS THE FUTURE
              </Typography>
            </Box>

            <Typography
              sx={{
                mt: 0.7,
                fontSize: ".62rem",
                fontWeight: 700,
                letterSpacing: ".08em",
                color: COLORS.gold,
              }}
            >
              HOPE • SERVICE • IMPACT
            </Typography>

            <Typography
              sx={{
                mt: 0.8,
                fontSize: ".68rem",
                color: COLORS.muted,
              }}
            >
              Secure account recovery
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default VerifyCode;